import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { CustomButton } from "../../../components/CustomButton";
import { hasData } from "../../../utils/commonUtils";
import typography from "../typography";
import { getAudioVideoContentById } from "../../../api/api.service";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { formatDuration } from "../utils";
import { ACTIVITY_SLOT_OPTIONS, RESET_CONTEXT_OPTIONS } from "../data";

export default function ViewContent() {
    const { id } = useParams();
    const history = useHistory();

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    const handleEditClick = () => {
        history.push(`${APP_ROUTES.EDIT_CONTENT_LIBRARY}/${id}`);
    };

    useEffect(() => {
        const fetchContent = async () => {
            if (!id) {
                setError("Invalid content ID");
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await getAudioVideoContentById(id);

                if (response) {
                    setData(response);
                } else {
                    setError("Content not found");
                }
            } catch (error) {
                console.error("Error fetching content details:", error);
                setError("Failed to load content details");
                toast.error("Failed to load content details");
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [id]);

    if (loading) {
        return <LoaderFallbackUI isLoader={loading} />;
    }

    if (error) {
        return (
            <div className="error-container">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.VIEW_CONTENT}
                    showToggle={false}
                />
                <div className="alert alert-danger m-3">
                    <h5>Error</h5>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!hasData(data)) {
        return (
            <div className="no-data-container">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.VIEW_CONTENT}
                    showToggle={false}
                />
                <div className="alert alert-info m-3">
                    <h5>No Data</h5>
                    <p>No content details available.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="edit-notification-heading">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.VIEW_CONTENT}
                    showToggle={false}
                />
                <div className="edit-notification-heading-button">
                    <CustomButton
                        title={typography.EDIT_CONTENT}
                        className="edit-notification-update-button"
                        onClick={handleEditClick}
                        type="button"
                    />
                </div>
            </div>
            <div className="notification-details-con">
                <Row className="notification-details-typo p-3">
                    <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                        <h6>{typography.TITLE}</h6>
                        <p>{data.title || "-"}</p>
                    </Col>
                    <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                        <h6>{typography.AUTHOR}</h6>
                        <p>{data.author || "-"}</p>
                    </Col>
                    <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                        <h6>{typography.CONTENT_TYPE}</h6>
                        <p>{data.contentType === "AUDIO" ? typography.AUDIO : typography.VIDEO}</p>
                    </Col>
                    <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                        <h6>{typography.STATE}</h6>
                        <p className="text-capitalize">{data.stateKey || "-"}</p>
                    </Col>
                    {data.activitySlot != null && (
                        <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                            <h6>{typography.ACTIVITY_SLOT}</h6>
                            <p>
                                {ACTIVITY_SLOT_OPTIONS.find((option) => option.value === data.activitySlot)?.label ??
                                    `Card ${data.activitySlot + 1}`}
                            </p>
                        </Col>
                    )}
                    <Col xs="6" xxl="3" xl="4" md="4" sm="6" className="mb-5">
                        <h6>{typography.DURATION}</h6>
                        <p>{formatDuration(data.duration)}</p>
                    </Col>
                </Row>

                {data.contexts?.length > 0 && (
                    <Row className="notification-details-typo p-3">
                        <Col xs="12">
                            <h6>Quick timing / context filters</h6>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                                {data.contexts.map((context) => (
                                    <span key={context} className="badge bg-light text-dark border">
                                        {RESET_CONTEXT_OPTIONS.find((option) => option.value === context)?.label ?? context}
                                    </span>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {data.description && (
                    <Row className="notification-details-typo p-3">
                        <Col xs="12">
                            <h6>{typography.DESCRIPTION}</h6>
                            <p>{data.description}</p>
                        </Col>
                    </Row>
                )}

                {data.goal && (
                    <Row className="notification-details-typo p-3">
                        <Col xs="12">
                            <h6>{typography.GOAL}</h6>
                            <p>{data.goal}</p>
                        </Col>
                    </Row>
                )}

                {data.teacherInstructions && (
                    <Row className="notification-details-typo p-3">
                        <Col xs="12">
                            <h6>{typography.TEACHER_INSTRUCTIONS}</h6>
                            <p>{data.teacherInstructions}</p>
                        </Col>
                    </Row>
                )}

                {data.thumbnailUrl && (
                    <Row className="notification-details-typo p-3">
                        <Col xs="12">
                            <h6>Thumbnail</h6>
                            {/* eslint-disable-next-line jsx-a11y/alt-text */}
                            <img src={data.thumbnailUrl} alt="" className="content-thumb-preview" />
                        </Col>
                    </Row>
                )}

                <div className="content-preview-con">
                    {data.contentType === "AUDIO" ? (
                        <audio controls src={data.mediaUrl} />
                    ) : (
                        <video controls poster={data.thumbnailUrl} src={data.mediaUrl} />
                    )}
                </div>

                <Row className="notification-details-typo p-3">
                    <Col xs="12">
                        <h6>{typography.TEACHER_GUIDE_VIDEO}</h6>
                        {data.teacherGuideVideoUrl ? (
                            <div className="content-preview-con">
                                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                <video controls src={data.teacherGuideVideoUrl} />
                            </div>
                        ) : (
                            <p>{typography.NOT_UPLOADED}</p>
                        )}
                    </Col>
                </Row>
            </div>
        </>
    );
}
